// Copyright (c) 2025 WiseTime. All rights reserved.

import { concat, Observable } from "rxjs"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { create } from "@bufbuild/protobuf"
import { catchError, filter, map, mergeMap, withLatestFrom } from "rxjs/operators"
import * as Slice from "../slices/timeline"
import * as Actions from "../actions"
import { showSnackBar } from "../../snackbar/actions"
import type { RootState } from "../../../app/store"
import { getTimesheetFieldLabel } from "../timesheet/timesheet-fields"
import { Code, from, Init, isConnectError, timelineService } from "../../../app/rpc"
import {
  SelectRowTimesheetFieldOptionRequestSchema,
  SelectRowTimesheetFieldOptionResponse,
} from "../../../generated/wisetime/user/timeline/v4/timeline_pb"
import {
  SelectedTimesheetFieldOptionSchema,
} from "../../../generated/wisetime/user/type/selected_timesheet_field_option_pb"

type SelectRowTimesheetFieldOptionRpc = (request: Init<typeof SelectRowTimesheetFieldOptionRequestSchema>) => Observable<SelectRowTimesheetFieldOptionResponse>

export const selectRowTimesheetFieldOption = (selectRowTimesheetFieldOptionRpc: SelectRowTimesheetFieldOptionRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.selectRowTimesheetFieldOption.match),
      withLatestFrom(state$),
      mergeMap(([action, state]) => selectRowTimesheetFieldOptionRpc(action.payload)
        .pipe(
          map(response => response.timeRows.map(timeRow => timeRow)),
          mergeMap((timeRows) => {
            const {
              siteId = "",
              timesheetFieldId = "",
              timesheetFieldOptionId = "",
            } = action.payload
            const actions: Action[] = [
              Slice.updateRowTimesheetFieldOption({
                timeRowIds: timeRows.map(tr => tr.id),
                selectedTimesheetFieldOption: create(SelectedTimesheetFieldOptionSchema, {
                  siteId,
                  timesheetFieldId,
                  timesheetFieldOptionId,
                }),
              }),
              Actions.listRecentTimesheetFieldOptions({ siteId }),
            ]
            const timesheetFieldOptionsCache = Slice.selectTimesheetFieldOptionsCache(state)
            if (timesheetFieldOptionsCache[timesheetFieldOptionId] === undefined) {
              actions.push(
                Actions.batchGetTimesheetFieldOptions({
                  ids: [timesheetFieldOptionId],
                  siteId,
                })
              )
            }
            return concat(actions)
          }),
          catchError(e => {
            const timesheetField = state.timeline.timesheetFieldsCache[action.payload.timesheetFieldId ?? ""]
            const fieldName = timesheetField
              ? getTimesheetFieldLabel(timesheetField)
              : t({ id: "msg_timesheet_field", message: "Timesheet field" }).toLowerCase()
            return handleError(e, fieldName)
          }),
        )
      ),
    )

const handleError = (e: unknown, fieldName: string): Observable<Action> => {
  const actions: Action[] = []
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.NotFound) {
      actions.push(
        showSnackBar({
          message: t({ 
            id: "msg_select_timesheet_field_option_not_found",
            message: "Failed to select {fieldName} value, {fieldName} or value not found.",
            values: { fieldName },
          }),
          messageType: "ERROR",
        }),
        Actions.onTimelineOutOfSync(),
      )
      return concat(actions)
    }
    if (code === Code.PermissionDenied) {
      actions.push(showSnackBar({
        message: t({ 
          id: "msg_select_timesheet_field_option_permission_denied",
          message: "Failed to select {fieldName} value. You do not have permission.",
          values: { fieldName },
        }),
        messageType: "ERROR",
      }))
      return concat(actions)
    }
    if (code === Code.FailedPrecondition) {
      actions.push(
        showSnackBar({
          message: t({ 
            id: "msg_select_row_timesheet_field_option_failed_precondition", 
            message: "Failed to select {fieldName} value. Time row may be out of sync.",
            values: { fieldName },
          }),
          messageType: "ERROR",
        }),
        Actions.onTimelineOutOfSync(),
      )
      return concat(actions)
    }
  }
  actions.push(showSnackBar({
    message: t({
      id: "msg_select_timesheet_field_option_failed",
      message: "Failed to select {fieldName} value. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
      values: { fieldName },
    }),
    messageType: "ERROR",
  }))
  return concat(actions)
}

const selectRowTimesheetFieldOptionRpc = (request: Init<typeof SelectRowTimesheetFieldOptionRequestSchema>): Observable<SelectRowTimesheetFieldOptionResponse> =>
  from(() => timelineService.selectRowTimesheetFieldOption(create(SelectRowTimesheetFieldOptionRequestSchema, request)))

export default selectRowTimesheetFieldOption(selectRowTimesheetFieldOptionRpc)
